import { useMemo, useState } from 'react';
import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import PageHeader from '@/components/common/PageHeader';
import { useAuth } from '@/contexts/AuthContext';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import { PERMISSIONS } from '@/types/auth';
import type { ProcessConfigurationDto } from '@/types/configuration';
import OverviewPanel from './panels/OverviewPanel';
import InventoryPanel from './panels/InventoryPanel';
import ProcurementPanel from './panels/ProcurementPanel';
import InboundQualityPanel from './panels/InboundQualityPanel';
import OutboundPanel from './panels/OutboundPanel';
import ProductionPanel from './panels/ProductionPanel';
import JobWorkPanel from './panels/JobWorkPanel';
import WeightProductionPanel from './panels/WeightProductionPanel';
import AdjustmentsPanel from './panels/AdjustmentsPanel';
import ApprovalsPanel from './panels/ApprovalsPanel';
import BillingPanel from './panels/BillingPanel';

// Same configKey/hiddenWhenConfigKey shape and AND/OR semantics as AppShell.tsx's NavLeaf and
// MastersPage.tsx's MasterTabDef — a third copy of the same small gate, deliberately not extracted
// into a shared helper yet (flagged as a candidate, not done automatically, to avoid unrequested
// refactor scope creep on an already large change). inventory/inboundQuality/adjustments stay
// tab-level-ungated: each bundles multiple independently-toggleable sub-flags, handled one level
// down inside its own panel instead.
interface CategoryTabDef {
  key: string;
  Panel: ComponentType;
  configKey?: keyof ProcessConfigurationDto | (keyof ProcessConfigurationDto)[];
  hiddenWhenConfigKey?: keyof ProcessConfigurationDto | (keyof ProcessConfigurationDto)[];
}

const CATEGORY_TABS: readonly CategoryTabDef[] = [
  { key: 'inventory', Panel: InventoryPanel },
  {
    key: 'procurement', Panel: ProcurementPanel,
    configKey: 'purchaseOrderEnabled', hiddenWhenConfigKey: 'thirdPartyLogisticsEnabled',
  },
  { key: 'inboundQuality', Panel: InboundQualityPanel },
  { key: 'outbound', Panel: OutboundPanel, configKey: 'dispatchEnabled' },
  {
    key: 'production', Panel: ProductionPanel,
    configKey: 'productionEnabled', hiddenWhenConfigKey: 'thirdPartyLogisticsEnabled',
  },
  {
    // Deliberately NOT hidden under 3PL (unlike Production above): a 3PL warehouse can legitimately
    // send a client's goods out for processing, which is how the Job Work nav itself is gated.
    key: 'jobWork', Panel: JobWorkPanel, configKey: 'jobWorkEnabled',
  },
  { key: 'weightProduction', Panel: WeightProductionPanel, configKey: 'weightProductionEnabled' },
  { key: 'adjustments', Panel: AdjustmentsPanel },
  { key: 'approvals', Panel: ApprovalsPanel, configKey: 'approvalWorkflowEnabled' },
  { key: 'billing', Panel: BillingPanel, configKey: ['thirdPartyLogisticsEnabled', 'billingEnabled'] },
];

export default function Dashboard() {
  const { t } = useTranslation('dashboard');
  const { user, hasPermission } = useAuth();
  const { data: processConfiguration } = useProcessConfiguration();
  const [tab, setTab] = useState(0);

  const canViewReports = hasPermission(PERMISSIONS.REPORT_VIEW);

  const passesConfigGate = (tabDef: (typeof CATEGORY_TABS)[number]) => {
    if (!('configKey' in tabDef) || !tabDef.configKey || !processConfiguration) return true;
    const keys = Array.isArray(tabDef.configKey) ? tabDef.configKey : [tabDef.configKey];
    return keys.every((key) => processConfiguration[key]);
  };
  const passesHiddenWhenGate = (tabDef: (typeof CATEGORY_TABS)[number]) => {
    if (!('hiddenWhenConfigKey' in tabDef) || !tabDef.hiddenWhenConfigKey || !processConfiguration) return true;
    const keys = Array.isArray(tabDef.hiddenWhenConfigKey) ? tabDef.hiddenWhenConfigKey : [tabDef.hiddenWhenConfigKey];
    return !keys.some((key) => processConfiguration[key]);
  };

  const tabs = useMemo(
    () => [
      { key: 'overview' as const, Panel: OverviewPanel },
      ...(canViewReports ? CATEGORY_TABS.filter((tabDef) => passesConfigGate(tabDef) && passesHiddenWhenGate(tabDef)) : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canViewReports, processConfiguration],
  );

  const activeIndex = Math.min(tab, tabs.length - 1);
  const ActivePanel = tabs[activeIndex]?.Panel ?? OverviewPanel;

  return (
    <>
      <PageHeader title={t('title')} subtitle={t('welcome', { name: user?.fullName ?? '' })} />

      {tabs.length > 1 ? (
        <Box sx={{ mb: 3 }}>
          <Tabs value={activeIndex} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
            {tabs.map((tabDef) => (
              <Tab key={tabDef.key} label={t(`tabs.${tabDef.key}`)} />
            ))}
          </Tabs>
        </Box>
      ) : undefined}

      <ActivePanel />
    </>
  );
}
